import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Trophy,
  Users,
  Shield,
  Award,
  Building2,
  Database,
  CreditCard,
  HelpCircle,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Languages,
} from "lucide-react";
import useAuthStore from "../../store/auth.store";
import { logout } from "../../api/auth.api";
import { useTranslation } from "../../i18n/useTranslation";
import WalletBadge from "../wallet/WalletBadge";
import ThemeToggle from "../common/ThemeToggle";
import GlobalSearch from "../common/GlobalSearch";

// Single source of truth for navigation. Used by AccountLayout + CricketLayout
// so every authenticated page has the same primary nav. Sections gate themselves
// on user role / feature flags so B2C users don't see B2B-only items.
export default function GlobalNav() {
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const { t, lang, setLanguage, languages } = useTranslation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!langMenuOpen) return;
    const onClick = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [langMenuOpen]);

  // Click-outside to close the user menu
  useEffect(() => {
    if (!userMenuOpen) return;
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [userMenuOpen]);

  const isOrg =
    user?.account_type === "organization" || user?.account_type_v2 === "B2B";
  const v2Role = user?.account_user_role;
  const legacyRole = user?.role;
  const canManageOrg =
    user?.is_superuser ||
    ["SUPER_ADMIN", "ADMIN"].includes(v2Role) ||
    ["OWNER", "ADMIN"].includes(legacyRole);
  // Founder-superuser only — hides the customer-facing nav items
  // (Ingest, wallet, Subscription) that don't apply when operating
  // the platform as admin.
  const isAdmin = !!user?.is_superuser;

  // Primary nav. NavLink handles the active highlight.
  const items = [
    { to: "/dashboard",            label: t("nav.dashboard"),    icon: LayoutDashboard, always: true },
    { to: "/cricket/matches",      label: t("nav.matches"),      icon: Trophy,          always: true },
    { to: "/cricket/players",      label: t("nav.players"),      icon: Users,           always: true },
    { to: "/cricket/teams",        label: t("nav.teams"),        icon: Shield,          always: true },
    { to: "/cricket/competitions", label: t("nav.competitions"), icon: Award,           always: true },
    { to: "/organisation-dashboard", label: t("nav.organisation"), icon: Building2,     show: isOrg && canManageOrg },
    { to: "/ingest",               label: "Ingest",              icon: Database,        show: !isAdmin && !!user?.is_ingest_allowed },
  ].filter((i) => i.always || i.show);

  const currentLangLabel = languages.find((l) => l.code === lang)?.native || "EN";

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
    clearAuth();
    // 2026-06-10 founder ask — after logout, the browser back button
    // from /login should land on the marketing page, not the now-
    // protected dashboard the user just came from. Replace the current
    // history entry with "/", then push /login on top. Stack becomes
    // [..., "/", "/login"] so back → "/" (landing).
    navigate("/", { replace: true });
    navigate("/login");
  };

  const initials = ((user?.first_name || user?.username || "?")[0] || "?").toUpperCase();
  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.username || "Account";

  const navItemClass = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
      isActive
        ? "bg-button-primary/15 text-text-primary font-semibold"
        : "text-text-secondary hover:text-text-primary hover:bg-bg-secondary/40"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-bar/95 backdrop-blur border-b border-border-default">
      <div className="flex items-center h-14 px-4">
        {/* Brand — text wordmark only. The PNG logo was removed
            2026-05-27 per founder ask (1536x1024 asset was dominating).
            2026-06-10 — brand link points to "/" (landing) per founder
            ask. Logged-in users see the landing's "Go to your dashboard"
            CTA in its navbar to step back into the app. */}
        <Link to="/" className="flex items-center gap-2 mr-4 shrink-0">
          <span className="text-text-primary font-bold tracking-tight">
            Zen<span className="text-button-primary">cricket</span>data
          </span>
          <span className="hidden lg:inline text-[9px] text-text-secondary/60 font-mono">v.2026-05-09</span>
        </Link>

        {/* Desktop nav (and tablet) */}
        <nav className="hidden md:flex items-center gap-1">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} className={navItemClass} end={item.to === "/dashboard"}>
                <Icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Right side: single flex row. 2026-06-05 — WalletBadge and the
            user-avatar menu are now always visible (no md: gate) so the
            mobile top bar looks like the desktop one — balance + identity
            at all times. Search / theme / language stay behind the
            hamburger on phones (still in the drawer). */}
        <div className="flex items-center gap-2 ml-auto">
          <div className="hidden lg:block w-72">
            <GlobalSearch />
          </div>
          {/* WalletBadge = Zencredits balance + 'Top up' pill. Hide
              for the founder-admin since they don't transact like a
              customer. */}
          {!isAdmin && <WalletBadge />}
          <div className="hidden md:flex"><ThemeToggle compact /></div>
          {/* Mobile hamburger sits between the always-visible right-cluster
              items so phones still get to the nav links + the language
              + theme controls via the drawer below. */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden h-8 w-8 inline-flex items-center justify-center rounded-md text-text-secondary hover:text-text-primary hover:bg-bg-secondary/40 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="relative hidden md:flex" ref={langMenuRef}>
            <button
              onClick={() => setLangMenuOpen((v) => !v)}
              className="flex items-center gap-1 h-8 px-2 rounded-md text-xs text-text-secondary hover:text-text-primary hover:bg-bg-secondary/40 transition-colors"
              title={t("common.language")}
            >
              <Languages className="w-3.5 h-3.5" />
              <span className="font-medium">{currentLangLabel}</span>
            </button>
            {langMenuOpen && (
              <div className="absolute top-10 right-0 w-44 bg-bg-primary border border-border-default rounded-lg shadow-xl py-1 z-50">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLanguage(l.code); setLangMenuOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-bg-secondary/40 ${
                      l.code === lang ? "text-button-primary font-semibold" : "text-text-primary"
                    }`}
                  >
                    <span>{l.native}</span>
                    <span className="text-text-secondary text-[10px] ml-2">{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen((v) => !v)}
              className="flex items-center gap-2 h-8 pl-1 pr-2 rounded-md hover:bg-bg-secondary/40 transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-button-primary text-white font-bold flex items-center justify-center text-xs">
                {initials}
              </div>
              <div className="text-right hidden lg:block leading-tight">
                <p className="text-xs font-semibold text-text-primary">{displayName}</p>
                <p className="text-[10px] text-text-secondary">{user?.email}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-text-secondary" />
            </button>

            {userMenuOpen && (
              <div className="absolute top-10 right-0 w-56 max-h-[calc(100vh-80px)] overflow-y-auto bg-bg-primary border border-border-default rounded-lg shadow-xl py-1 z-50">
                <Link
                  to="/dashboard"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-secondary/40"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Link>
                {!isAdmin && (
                  <Link
                    to="/subscriptions"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-secondary/40"
                  >
                    <CreditCard className="w-4 h-4" />
                    Subscription
                  </Link>
                )}
                <Link
                  to="/help/scorer-guide"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-secondary/40"
                >
                  <HelpCircle className="w-4 h-4" />
                  Help & guides
                </Link>
                <div className="border-t border-border-default my-1" />
                <button
                  onClick={handleLogout}
                  className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-rose-300 hover:bg-rose-500/10"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border-default bg-bar">
          <nav className="flex flex-col py-2">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-3 text-sm border-l-2 ${
                      isActive
                        ? "border-button-primary bg-button-primary/10 text-text-primary font-semibold"
                        : "border-transparent text-text-secondary"
                    }`
                  }
                  end={item.to === "/dashboard"}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              );
            })}
            <div className="border-t border-border-default mt-2 py-2">
              {!isAdmin && (
                <Link to="/subscriptions" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-text-secondary">
                  <CreditCard className="w-4 h-4" /> Subscription
                </Link>
              )}
              <Link to="/help/scorer-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-text-secondary">
                <HelpCircle className="w-4 h-4" /> Help & guides
              </Link>
              <div className="px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-text-secondary">Theme</span>
                <ThemeToggle />
              </div>
              <button onClick={handleLogout} className="w-full text-left flex items-center gap-2 px-4 py-3 text-sm text-rose-300">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
